import React, { useEffect, useRef, useState } from 'react';
import * as echarts from 'echarts';
import { Card, Spinner } from 'react-bootstrap';
import { hourlyAvg } from 'hooks/CallApi'; // API 함수 import
import { useAppContext } from 'providers/AppProvider';
import { getCompanyColor } from 'helpers/companyColors';

const processingTimeThreshold = 0.5;

const SiteAverageApiProcess = () => {
  const { config: { isDemo } } = useAppContext();
  const chartRef = useRef(null);
  const [chart, setChart] = useState(null);
  const [data, setData] = useState([]);
  const [isLoading, setIsLoading] = useState(true);

  // 데이터 로드
  useEffect(() => {
    let cancelled = false;
    const fetchData = async () => {
      setIsLoading(true);
      try {
        const clientTimeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;

        const response = await hourlyAvg({ timeZone: clientTimeZone, type: 'api', demo: isDemo });
        if (cancelled) return;
        const apiData = response.data;

        if (!apiData || apiData.length === 0) {
          if (cancelled) return;
          setData([]);
          return;
        }
        const formattedData = apiData.map((company) => ({
          name: company.companyName,
          hourlyData: Array.from({ length: 24 }, (_, hour) => {
            const hourData = company.hourlyAvgDataList.find((item) => item.hour === hour);
            return hourData ? hourData.hourlyAvgDuration : null;
          }),
        }));
        if (cancelled) return;
        setData(formattedData);
      } catch (error) {
        if (cancelled) return;
        console.error('[API-hourlyAvg] 데이터 호출 실패:', error);
      } finally {
        if (!cancelled) setIsLoading(false);
      }
    };

    fetchData();
    return () => {
      cancelled = true;
    };
  }, [isDemo]);

  useEffect(() => {
    if (!chartRef.current || isLoading) return;
    const chart = echarts.init(chartRef.current);

    const generateSeries = () =>
      data.map((company, index) => ({
        name: company.name,
        type: 'line',
        smooth: true, // 곡선 렌더링
        lineStyle: { width: 3 }, // 선 두께감
        areaStyle: {
          opacity: 0.1, // 은은한 하단 그라데이션 영역
        },
        data: company.hourlyData,
        markPoint: {
          data: company.hourlyData
            .map((value, hour) =>
              value > 0.5
                ? { value, xAxis: hour, yAxis: value }
                : null
            )
            .filter(Boolean),
          symbol: 'circle',
          symbolSize: 7,
          itemStyle: { color: 'yellow' },
          label: { show: false },
        },
      }));

    // 차트 옵션
    const option = {
      color: data.map(company => getCompanyColor(company.name)),
      tooltip: {
        trigger: 'axis',
        transitionDuration: 0,
        formatter: (params) => {
          let html = `<div><strong style="font-size:13px; color:#666">${params[0].axisValue}</strong></div>`;
          const seen = new Set();
          params.forEach((param) => {
            if (param.seriesName !== '지연 기준선' && !seen.has(param.seriesName) && param.value !== undefined) {
              seen.add(param.seriesName);
              const val = Number(param.value);
              html += `<div style="display:flex; justify-content:space-between; align-items:center;">
                <span style="margin-right: 15px;">${param.marker} ${param.seriesName}</span>
                <strong style="color:#333">${!isNaN(val) ? val.toFixed(3) : 0} 초</strong>
              </div>`;
            }
          });
          html += `<div style="margin-top: 6px; border-top: 1px dashed #ccc; padding-top: 6px;">
            <span style="display:inline-block;margin-right:5px;border-radius:10px;width:10px;height:10px;background-color:#3b82f6;"></span> 
            지연 기준선 <strong style="color:#3b82f6; float:right;">${processingTimeThreshold.toFixed(3)} 초</strong>
          </div>`;
          return html;
        }
      },
      legend: {
        data: data.map((company) => company.name),
        bottom: '5%',
      },
      xAxis: {
        type: 'category',
        data: Array.from({ length: 24 }, (_, hour) => `${hour.toString().padStart(2, '0')}시`),
      },
      yAxis: {
        type: 'value',
        min: 0,
        max: (value) => Math.max(1.0, value.max),
        axisLabel: { formatter: '{value} 초' },
      },
      series: generateSeries().map(series => ({
        ...series,
        markLine: {
          symbol: 'none',
          data: [{ yAxis: processingTimeThreshold, name: '지연 기준선' }],
          lineStyle: {
            color: '#3b82f6',
            type: 'dashed',
            width: 2
          },
          label: { show: false }
        }
      })),
      grid: {
        left: '10%',
        right: '5%',
        bottom: '25%',
        top: '5%',
      },

    };
    chart.setOption(option, true);
    chart.on('globalout', () => {
      chart.dispatchAction({ type: 'hideTip' });
    });
    const resizeChart = () => chart.resize();
    window.addEventListener('resize', resizeChart);

    return () => {
      window.removeEventListener('resize', resizeChart);
      chart.dispose();
    };
  }, [data, isLoading]);

  return (
    <Card className="h-md-100" style={{ borderRadius: '24px', boxShadow: '0 4px 20px rgba(0,0,0,0.03)', border: 'none', backgroundColor: '#FFFFFF' }}>
      <Card.Header className="pb-0 d-flex align-items-center" style={{ borderBottom: 'none', backgroundColor: 'transparent', padding: '20px 24px' }}>
        <h5 style={{ margin: 0, fontWeight: 700, color: '#111827' }}>시간별 API 평균 처리속도</h5>
        <span
          style={{
            marginLeft: '8px',
            marginTop: 0,
            marginBottom: 0,
            padding: '2px 8px',
            backgroundColor: '#2C7BE5',
            color: 'white',
            borderRadius: '4px',
            fontSize: '12px',
            fontWeight: 'bold',
          }}
        >
          최근 1주일        </span>
      </Card.Header>
      <Card.Body>
        {isLoading && (
          <div style={{ width: '100%', height: '15rem', display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
            <Spinner animation="border" role="status">
              <span className="visually-hidden">Loading...</span>
            </Spinner>
          </div>
        )}
        <div 
          ref={chartRef} 
          onMouseLeave={() => chartRef.current && echarts.getInstanceByDom(chartRef.current)?.dispatchAction({ type: 'hideTip' })} 
          style={{ width: '100%', height: '20rem', display: isLoading ? 'none' : 'block' }} 
        />
      </Card.Body>
    </Card>
  );
};

export default SiteAverageApiProcess;
